import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../Button/Button';
import { Switch } from '../Switch/Switch';
import { CompanySelector } from './CompanySelector';
import type { MasterCrudToolbarProps, ToolbarViewType, MasterCrudCompany } from './MasterCrud.types';
import {
  SearchIcon,
  FilterIcon,
  ChevronDownIcon,
  GridIcon,
  ListIcon,
  EllipsisVerticalIcon,
  DocumentArrowDownIcon,
  BackIcon,
} from './icons';

/**
 * MasterCrudToolbar - Barra de herramientas del listado (alineada al prototipo).
 *
 * Izquierda: título + CompanySelector (combobox azul) debajo.
 * Derecha: búsqueda rápida (con toggle de búsqueda avanzada), contenido extra (botón Crear)
 * y menú "Más opciones" (kebab vertical) con: alcance multicompañía, modo de visualización y exportar.
 */
export const MasterCrudToolbar: React.FC<MasterCrudToolbarProps> = ({
  title,
  searchPlaceholder,
  searchValue = '',
  onSearchChange,
  showSearch = true,
  viewType = 'list',
  onViewChange,
  showViewToggle = true,
  showAdvancedSearch = false,
  extraContent,
  className = '',
  globalActions = [],
  batchActions = [],
  hasSelection = false,
  isAdvancedSearchActive = false,
  onAdvancedSearchToggle,
  activeByCompany = false,
  companyRequired = false,
  companies = [],
  selectedCompany,
  onCompanyChange,
  showExport = false,
  onExport,
  viewScope = 'all',
  onViewScopeChange,
  showViewScope = false,
  onBack,
  t,
}) => {
  // La búsqueda rápida siempre muestra el placeholder "Buscar": internamente busca por
  // todos los campos marcados como buscables, sin importar el campo/estado seleccionado.
  const finalSearchPlaceholder = searchPlaceholder || t('search', 'Buscar');

  // ===== MENÚ "MÁS OPCIONES" =====
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!moreOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMoreOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [moreOpen]);

  // ===== ACCIONES =====
  const currentActions = hasSelection ? batchActions : globalActions;
  const directActions = currentActions.filter((a) => a.displayType === 'direct');
  const menuActions = currentActions.filter((a) => a.displayType !== 'direct');

  const handleViewChange = (view: ToolbarViewType) => onViewChange?.(view);
  const handleCompanySelect = (company: MasterCrudCompany) => onCompanyChange?.(company, undefined);

  const hasMoreMenu = showViewToggle || showExport || (showViewScope && activeByCompany) || menuActions.length > 0;

  return (
    <div className={`space-y-4 w-full ${className}`}>
      {/* Barra de navegación: enlace "Volver" (prototipo) */}
      <nav>
        <button
          type="button"
          onClick={() => (onBack ? onBack() : window.history.back())}
          className="inline-flex items-center gap-1 text-sm text-content-tertiary dark:text-dark-content-tertiary hover:text-[#1355b7] transition-colors"
        >
          <BackIcon className="w-4 h-4" />
          <span>{t('back', 'Volver')}</span>
        </button>
      </nav>

      {/* Fila: título + acciones */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 w-full">
      {/* Izquierda: título + selector de compañía debajo */}
      <div className="flex flex-col items-start gap-1.5 min-w-0">
        <h1 className="text-xl font-semibold tracking-tight text-[#1355b7] dark:text-primary-600 truncate max-w-full">{title}</h1>
        {activeByCompany && companies.length > 0 && (
          <CompanySelector
            companies={companies}
            selectedCompany={selectedCompany}
            onCompanyChange={handleCompanySelect}
            readOnly={companyRequired}
            t={t}
          />
        )}
      </div>

      {/* Derecha: búsqueda + Crear + kebab */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {showSearch && (
          <div className="relative flex items-center">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-tertiary dark:text-dark-content-tertiary pointer-events-none">
              <SearchIcon className="w-3.5 h-3.5" />
            </span>
            <input
              type="search"
              placeholder={finalSearchPlaceholder}
              value={searchValue}
              autoComplete="off"
              disabled={isAdvancedSearchActive}
              onChange={(e) => onSearchChange?.(e.target.value)}
              className="pl-8 pr-10 h-9 w-64 text-sm border border-border-primary dark:border-dark-border-primary rounded-md bg-white dark:bg-dark-bg-primary text-content-primary dark:text-dark-content-primary placeholder:text-content-tertiary focus:outline-none focus:border-[#93d1fd] focus:ring-[3px] focus:ring-primary-500/10 transition-colors disabled:opacity-60"
            />
            {showAdvancedSearch && (
              <button
                type="button"
                onClick={onAdvancedSearchToggle}
                aria-expanded={isAdvancedSearchActive}
                title={isAdvancedSearchActive ? t('hideAdvancedSearch', 'Ocultar búsqueda avanzada') : t('showAdvancedSearch', 'Mostrar búsqueda avanzada')}
                className={`absolute right-1.5 inline-flex items-center justify-center w-7 h-7 rounded-md transition-colors ${
                  isAdvancedSearchActive
                    ? 'bg-primary-600 text-white'
                    : 'text-content-tertiary hover:text-primary-600 hover:bg-[#eff8ff] dark:hover:bg-primary-900/20'
                }`}
              >
                {isAdvancedSearchActive
                  ? <ChevronDownIcon className="w-4 h-4 rotate-180" />
                  : <FilterIcon className="w-4 h-4" />}
              </button>
            )}
          </div>
        )}

        {/* Acciones globales directas (poco comunes; el prototipo no las usa por defecto) */}
        {directActions.map((action) => {
          const actionName = typeof action.name === 'function' ? action.name(null) : action.name;
          const actionIcon = typeof action.icon === 'function' ? action.icon(null) : action.icon;
          return (
            <Button
              key={action.id || actionName}
              type="outline"
              size="base"
              leftIcon={actionIcon}
              onClick={() => action.action?.([])}
              disabled={typeof action.disabled === 'function' ? action.disabled(null as any) : action.disabled}
            >
              {actionName}
            </Button>
          );
        })}

        {/* Botón Crear u otro contenido extra */}
        {extraContent}

        {/* Menú "Más opciones" (kebab vertical, sobre la tabla) */}
        {hasMoreMenu && (
          <div className="relative" ref={moreRef}>
            <button
              type="button"
              onClick={() => setMoreOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={moreOpen}
              title={t('moreOptions', 'Más opciones')}
              className="inline-flex items-center justify-center w-9 h-9 border border-border-primary dark:border-dark-border-primary rounded-md bg-background-secondary dark:bg-dark-bg-secondary hover:bg-[#ebebec] dark:hover:bg-dark-bg-primary text-content-tertiary transition-colors"
            >
              <EllipsisVerticalIcon className="w-4 h-4" />
            </button>

            {moreOpen && (
              <div role="menu" className="absolute right-0 top-full mt-1 w-64 bg-white dark:bg-dark-bg-primary border border-border-primary dark:border-dark-border-primary rounded-md shadow-md z-50 py-1">
                {/* Alcance de filas (multicompañía por sobrescritura).
                    Un único checkbox: marcado = solo la compañía seleccionada; desmarcado = todos. */}
                {showViewScope && activeByCompany && (
                  <div className="px-1 py-1 border-b border-border-primary dark:border-dark-border-primary">
                    <p className="px-2 pt-1 pb-1.5 text-[11px] font-semibold text-content-tertiary tracking-[0.3px]">{t('recordsToShow', 'Registros a mostrar')}</p>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={viewScope === 'company'}
                      onClick={() => onViewScopeChange?.(viewScope === 'company' ? 'all' : 'company')}
                      className="w-full flex items-center justify-between gap-3 px-2 py-2 rounded text-sm text-content-primary dark:text-dark-content-primary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary transition-colors"
                    >
                      <span className="flex-1 text-left select-none">
                        {t('viewCompanyOnly', 'Ver solo registros de la compañía seleccionada')}
                      </span>
                      <Switch
                        size="sm"
                        checked={viewScope === 'company'}
                        onChange={() => { }}
                        className="pointer-events-none shrink-0"
                      />
                    </button>
                  </div>
                )}

                {/* Modo de visualización (Listado / Tarjetas) */}
                {showViewToggle && (
                  <div className="px-3 py-2 border-b border-border-primary dark:border-dark-border-primary">
                    <p className="text-[11px] font-semibold text-content-tertiary tracking-[0.3px] mb-2">{t('displayMode', 'Modo de visualización')}</p>
                    <div className="inline-flex w-full border border-border-primary dark:border-dark-border-primary rounded-md overflow-hidden text-sm">
                      <button
                        type="button"
                        onClick={() => handleViewChange('list')}
                        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 transition-colors ${viewType === 'list' ? 'bg-primary-600 text-white' : 'bg-white dark:bg-dark-bg-primary text-content-tertiary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary'}`}
                      >
                        <ListIcon className="w-4 h-4" /><span>{t('listView', 'Listado')}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleViewChange('grid')}
                        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 transition-colors ${viewType === 'grid' ? 'bg-primary-600 text-white' : 'bg-white dark:bg-dark-bg-primary text-content-tertiary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary'}`}
                      >
                        <GridIcon className="w-4 h-4" /><span>{t('cardView', 'Tarjetas')}</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Exportar (solo si showExport) */}
                {showExport && (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => { onExport?.(); setMoreOpen(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm text-content-primary dark:text-dark-content-primary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary text-left transition-colors"
                  >
                    <DocumentArrowDownIcon className="w-4 h-4 text-content-tertiary" />
                    <span>{t('export', 'Exportar')}</span>
                  </button>
                )}

                {/* Acciones de menú personalizadas */}
                {menuActions.length > 0 && (showViewToggle || showExport || (showViewScope && activeByCompany)) && (
                  <hr className="my-1 border-border-primary dark:border-dark-border-primary" />
                )}
                {menuActions.map((action) => {
                  const actionName = typeof action.name === 'function' ? action.name(null) : action.name;
                  const actionIcon = typeof action.icon === 'function' ? action.icon(null) : action.icon;
                  const isDelete = actionName.toLowerCase().includes('eliminar') || actionName.toLowerCase().includes('delete');
                  return (
                    <button
                      key={action.id || actionName}
                      type="button"
                      role="menuitem"
                      disabled={typeof action.disabled === 'function' ? action.disabled(null as any) : action.disabled}
                      onClick={() => { action.action?.([]); setMoreOpen(false); }}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-left transition-colors hover:bg-background-secondary dark:hover:bg-dark-bg-secondary ${isDelete ? 'text-error-content' : 'text-content-primary dark:text-dark-content-primary'}`}
                    >
                      {actionIcon && <span className="w-4 h-4 flex-shrink-0 text-content-tertiary">{actionIcon}</span>}
                      <span>{actionName}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      </div>
    </div>
  );
};
